import React, { useState, useEffect, useCallback, useRef } from 'react';
import { alertErpia } from './ConfirmDialog';
import { useFeatures, saveFeatures } from '../services/features';
import SourceursSourcia from './SourceursSourcia';
import LinkedInModule from './config/LinkedInModule';
import AssistantSiteModule from './config/AssistantSiteModule';
import { ancre } from '../tutoriel/ancres';
import { useNavigate } from 'react-router-dom';
import APIService from '../services/api';

const BASE = 'https://besoins-tat-production.up.railway.app';
const BI = ({ icon, style }) => <i className={`bi bi-${icon}`} style={style} />;
const authH = () => ({ Authorization: `Bearer ${APIService.getToken()}` });

// ── Catalogue des applications ──────────────────────────────────────────────
// Chaque app est adossée à un feature flag org : l'activer ici équivaut à
// basculer le flag (PUT /config/features) — les écrans/boutons gated par
// useFeature(flag) s'allument/s'éteignent immédiatement (ex. « Diffuser aux
// sourceurs » sur le dossier besoin quand sourcia est activée).
const APPS = [
  {
    key: 'sourcia',
    name: 'sourcia',
    tagline: 'Marketplace de sourceurs freelances',
    category: 'Connecteurs',
    price: 'Inclus',
    monogram: 'so',
    description: 'Diffusez vos besoins à un réseau de sourceurs freelances (annonce anonymisable champ par champ, réf publique SC-*), recevez leurs propositions de profils directement dans le dossier besoin, et acceptez-les en un clic — le profil devient candidat. Rémunération sourceur en cas de placement : taux et assiette réglables dans le paramétrage ci-dessous.',
    unlocks: ['Bouton « Diffuser aux sourceurs » sur chaque besoin', 'Onglet « Propositions sourceurs » (accepter / refuser)', 'Validation des inscriptions sourceurs', 'Durée de propriété des CV (réglage)'],
  },
  {
    key: 'ressources',
    name: 'timesheet',
    tagline: 'CRA, congés & espace ressource',
    category: 'Gestion',
    price: 'Inclus',
    monogram: 'ts',
    description: 'Gérez vos consultants en mission : saisie des CRA (grille jour / demi-jour), soumission et validation client avec verrouillage du mois, demandes de congés, notes de frais avec justificatifs, documents. Chaque consultant lié à un compte au rôle « ressource » accède à son espace dédié (Mon espace) — les managers pilotent tout depuis l\'écran Gestion → Ressources.',
    unlocks: ['Écran « Ressources » (section Gestion du menu)', 'Validation client des CRA (verrouillage du mois)', 'Espace « Mon espace » pour les consultants liés (Timesheet, Congés, Frais, Documents)', 'Création de ressources et liaison des comptes'],
  },
  {
    key: 'crm',
    name: 'crm',
    tagline: 'Clients, contacts & missions',
    category: 'Gestion',
    price: 'Inclus',
    monogram: 'cr',
    description: 'Le pilier commercial de l\'ERP : fiches clients avec leurs contacts, et missions (client, ressource liée, TJM, période, taux, statut). La mission est la source de vérité de la rémunération — le wizard de conversion ressource s\'y réfère (« Selon mission (CRM) ») et les timesheets comme la facturation s\'appuieront dessus.',
    unlocks: ['Écran « CRM » (section Gestion du menu)', 'Fiches clients & contacts', 'Missions : TJM, période, taux, ressource liée', 'Socle des timesheets facturables et de la facturation à venir'],
  },
  {
    key: 'temptraining',
    name: 'temptraining',
    tagline: 'Formations & financement automatique',
    category: 'Connecteurs',
    price: 'Inclus',
    monogram: 'tt',
    description: 'Connectez la formation continue de vos ressources à temptraining : chaque formation planifiée depuis la fiche d\'une ressource (Gestion → Ressources → Formation) part automatiquement en demande de financement. L\'historique et le statut du financement reviennent sur la fiche.',
    unlocks: ['Demande de financement automatique à la planification d\'une formation', 'Statut du financement sur la fiche ressource (onglet Formation)', 'Historique de formation consolidé'],
  },
  {
    key: 'tatlease',
    name: 'TATLease',
    tagline: 'Gestion de flotte de véhicules',
    category: 'Gestion',
    price: 'Inclus',
    monogram: 'tl',
    description: 'Pilotez la flotte de véhicules mise à disposition de vos collaborateurs sans quitter l\'ERP : contrats de leasing, échéances, entretiens et affectations. L\'application s\'ouvre directement dans l\'ERP, connexion automatique avec votre compte.',
    unlocks: ['Entrée « Gestion de flotte » dans le menu', 'Connexion automatique (aucun mot de passe à retenir)', 'Véhicules, contrats et échéances au même endroit'],
  },
  {
    key: 'docsign',
    name: 'signd',
    tagline: 'Signature électronique de documents',
    category: 'Documents',
    price: 'Inclus',
    monogram: 'ds',
    description: 'Envoyez vos documents en signature électronique directement depuis l\'ERP (contrats, NDA…). Chaque envoi ouvre une fenêtre de préparation, le signataire reçoit un lien sécurisé, et le document signé revient dans le dossier. Socle requis par le module NDA.',
    unlocks: ['Envoi de contrats en signature depuis la fiche candidat', 'Socle de signature pour le module NDA', 'Suivi des signatures', 'Connexion à signd en un clic (clé unique par organisation)'],
  },
  {
    key: 'nda',
    name: 'nda',
    tagline: 'NDA automatique par opportunité',
    category: 'Documents',
    price: 'Inclus',
    monogram: 'nd',
    requires: ['docsign'],
    description: 'Générez et envoyez le NDA d\'une opportunité en un clic depuis le candidat positionné sur un besoin : le template DOCX de votre organisation est rempli automatiquement (client du besoin, candidat, date…) puis part en signature via signd. Un template par défaut erpia est fourni — remplacez-le par le vôtre.',
    unlocks: ['Bouton « Envoyer le NDA » sur chaque candidat positionné (dans le besoin)', 'Template DOCX de l\'organisation (variables injectées du besoin)', 'Envoi en signature via signd'],
  },
  {
    key: 'linkedin_publication',
    name: 'linkedin',
    tagline: 'Publier vos annonces sur LinkedIn',
    category: 'Connecteurs',
    price: 'Inclus',
    monogram: 'in',
    description: 'Chaque recruteur relie son compte LinkedIn une fois (deux mois, ERPIA prévient avant l\'échéance) et publie l\'annonce d\'un besoin sur son fil en un clic : le texte est préparé depuis la fiche de poste, le nom du client masqué, avec le lien pour postuler sur ERPIA. Pour une offre dans l\'onglet Emplois, ERPIA prépare le texte et le lien à coller dans LinkedIn. La publication au nom de la page entreprise s\'ouvrira quand LinkedIn l\'aura validée.',
    unlocks: ['Bouton « Publier sur LinkedIn » sur chaque besoin', 'Connexion du compte LinkedIn de chaque recruteur, rappel avant échéance', 'Texte prêt à coller pour une offre de l\'onglet Emplois, avec le lien pour postuler', 'Historique des publications par besoin'],
  },
  {
    key: 'assistant_site',
    name: 'assistant du site',
    tagline: 'Le chatbot de votre site, nourri par ERPIA',
    category: 'Connecteurs',
    price: 'Inclus',
    monogram: 'as',
    description: 'Le chatbot de votre site web (page Carrières, formation, apprentissages) répond avec ce que vous écrivez dans ERPIA : vos consignes et vos informations — conditions, durées, salaires, comment postuler. Il ne connaît rien d\'autre, renvoie vers votre formulaire de contact dès qu\'il ne sait pas, et ne demande aucune donnée personnelle. ERPIA vous donne une ligne à coller dans votre site — la bulle de chat, à votre couleur — et vous lisez ici ce que les visiteurs demandent.',
    unlocks: ['Une ligne à coller dans votre site : la bulle de chat, à votre couleur (ou l\'adresse, pour un site qui a déjà son widget)', 'Un éditeur de ce que l\'assistant sait et de son ton, avec l\'essai en direct', 'Les sites autorisés, un plafond de messages par jour, une clé qui se renouvelle', 'Le journal de ce que les visiteurs demandent'],
  },
];

// Les clés des modules : ils s'activent et se règlent ICI, pas dans la liste
// « Fonctionnalités » de la Configuration (retour du 12.10 : « la conf dans
// son module »).
export const CLES_MODULES = APPS.map(a => a.key);

// Monogramme carré (logo sourcia : fond sombre, mono, point rouge)
const Monogram = ({ text, size = 44 }) => (
  <span style={{ width: size, height: size, borderRadius: Math.round(size / 4.4), background: 'var(--text-strong)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', position: 'relative', flex: 'none' }}>
    <span style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: Math.round(size * 0.38), fontWeight: 600, color: 'var(--bg)' }}>{text}</span>
    <span style={{ position: 'absolute', bottom: Math.round(size * 0.16), right: Math.round(size * 0.16), width: Math.max(3, Math.round(size * 0.11)), height: Math.max(3, Math.round(size * 0.11)), borderRadius: 1, background: 'var(--brand)' }} />
  </span>
);

export default function Marketplace({ showNotification, currentUser }) {
  const features = useFeatures();
  const navigate = useNavigate();
  const [openApp, setOpenApp] = useState(null); // key de l'app ouverte en détail
  const [toggling, setToggling] = useState(null);

  // Arrivée sur un module par l'adresse (#/marketplace?module=…), ou retour
  // de LinkedIn (?linkedin=connecte|refuse|erreur&raison=…) : la page du
  // module s'ouvre, l'issue est dite, l'adresse est nettoyée.
  useEffect(() => {
    let q;
    try { q = new URLSearchParams(String(window.location.hash || '').split('?')[1] || ''); } catch { return; }
    const issue = q.get('linkedin');
    const module = q.get('module') || (issue ? 'linkedin_publication' : null);
    if (!module || !APPS.some(a => a.key === module)) return;
    setOpenApp(module);
    if (issue) {
      showNotification?.(issue === 'connecte' ? 'Compte LinkedIn connecté.' : (q.get('raison') || 'Connexion LinkedIn non faite.'), issue === 'connecte' ? 'success' : 'error');
    }
    navigate('/marketplace', { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Activée = flag présent ET non désactivé (état ORG, comme les toggles Config)
  const isActive = (key) => { const f = features?.[key]; return !!f && f.enabled !== false; };

  const toggleApp = async (app) => {
    const next = !isActive(app.key);
    // Dépendances : impossible d'activer une app dont le socle est éteint ;
    // désactiver un socle prévient que les apps dépendantes cessent de marcher.
    if (next && Array.isArray(app.requires)) {
      const missing = app.requires.filter(k => !isActive(k));
      if (missing.length) { showNotification?.(`Activez d'abord ${missing.join(', ')} — ${app.name} en dépend.`, 'error'); return; }
    }
    if (!next) {
      const dependents = APPS.filter(a => Array.isArray(a.requires) && a.requires.includes(app.key) && isActive(a.key));
      if (dependents.length) showNotification?.(`Attention : ${dependents.map(a => a.name).join(', ')} dépend(ent) de ${app.name} et cesseront de fonctionner.`, 'error');
    }
    setToggling(app.key);
    try {
      await saveFeatures({ [app.key]: next });
      showNotification?.(next
        ? `${app.name} activée — les fonctionnalités liées sont disponibles`
        : `${app.name} désactivée — masquée pour les utilisateurs`, 'success');
    } catch (e) { showNotification?.('Erreur : ' + e.message, 'error'); }
    finally { setToggling(null); }
  };

  const card = { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '14px' };
  const app = APPS.find(a => a.key === openApp);

  // ── Détail d'une app ──
  if (app) {
    const active = isActive(app.key);
    const busy = toggling === app.key;
    return (
      <div style={{ fontFamily: "'IBM Plex Sans', system-ui, sans-serif" }}>
        <button onClick={() => setOpenApp(null)} style={{ display: 'inline-flex', alignItems: 'center', gap: '7px', padding: '8px 13px', borderRadius: '8px', border: '1px solid var(--input-border)', background: 'var(--ghost-bg)', color: 'var(--text)', fontFamily: 'inherit', fontSize: '13px', fontWeight: 600, cursor: 'pointer', marginBottom: '18px' }}>
          <BI icon="chevron-left" /> Marketplace
        </button>

        <div style={{ ...card, padding: '22px 24px', display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap', marginBottom: '16px' }}>
          <Monogram text={app.monogram} size={52} />
          <div style={{ flex: 1, minWidth: '220px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span style={{ fontFamily: "'Archivo',sans-serif", fontSize: '22px', fontWeight: 800, letterSpacing: '-0.02em', color: 'var(--text-strong)' }}>{app.name}<span style={{ color: 'var(--brand)' }}>.</span></span>
              <span style={{ fontSize: '11px', fontWeight: 600, padding: '3px 9px', borderRadius: '6px', background: active ? 'rgba(45,200,110,0.15)' : 'var(--chip-bg)', color: active ? '#16a34a' : 'var(--text-2)' }}>{active ? 'Activée' : 'Désactivée'}</span>
            </div>
            <div style={{ fontSize: '13px', color: 'var(--text-2)', marginTop: '3px' }}>{app.tagline} · {app.category} · <span style={{ fontFamily: "'IBM Plex Mono',monospace" }}>{app.price}</span></div>
            {Array.isArray(app.requires) && app.requires.length > 0 && (
              <div style={{ marginTop: '6px', display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                {app.requires.map(k => (
                  <span key={k} style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', fontSize: '11px', fontWeight: 600, padding: '3px 9px', borderRadius: '6px', background: isActive(k) ? 'rgba(45,200,110,0.15)' : 'var(--danger-bg)', color: isActive(k) ? '#16a34a' : 'var(--danger-fg)' }}>
                    <BI icon={isActive(k) ? 'check-circle' : 'exclamation-triangle'} style={{ fontSize: '10px' }} /> dépend de {k}{!isActive(k) && ' (désactivé)'}
                  </span>
                ))}
              </div>
            )}
          </div>
          <button onClick={() => toggleApp(app)} disabled={busy}
            style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '10px 18px', borderRadius: '9px', border: 'none', fontFamily: 'inherit', fontSize: '13.5px', fontWeight: 600, cursor: busy ? 'wait' : 'pointer', opacity: busy ? 0.7 : 1, background: active ? 'var(--ghost-bg)' : 'var(--brand)', color: active ? 'var(--text)' : '#fff', ...(active ? { border: '1px solid var(--input-border)' } : {}) }}>
            <BI icon={busy ? 'arrow-repeat' : active ? 'pause-circle' : 'power'} style={busy ? { animation: 'ats-spin .7s linear infinite' } : undefined} />
            {busy ? '…' : active ? 'Désactiver' : 'Activer'}
          </button>
        </div>

        <div style={{ ...card, padding: '20px 24px', marginBottom: '16px' }}>
          <div style={{ fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-3)', marginBottom: '10px' }}>À propos</div>
          <p style={{ margin: 0, fontSize: '13.5px', lineHeight: 1.6, color: 'var(--text)' }}>{app.description}</p>
          <div style={{ fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-3)', margin: '16px 0 8px' }}>Ce que l'activation débloque</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {app.unlocks.map((u, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: 'var(--text-2)' }}>
                <BI icon={active ? 'check-circle-fill' : 'circle'} style={{ color: active ? '#16a34a' : 'var(--text-faint)', fontSize: '13px' }} /> {u}
              </div>
            ))}
          </div>
        </div>

        {/* Paramétrage de l'app — directement ici (plus dans Config) */}
        {app.key === 'sourcia' && (active ? (
          <div style={{ background: '#fff', border: '1px solid var(--border)', borderRadius: '14px', padding: '24px' }}>
            <SourceursSourcia showNotification={showNotification} />
          </div>
        ) : (
          <div style={{ ...card, padding: '28px 24px', textAlign: 'center' }}>
            <BI icon="lock" style={{ fontSize: '20px', color: 'var(--text-faint)' }} />
            <p style={{ margin: '8px 0 0', fontSize: '13px', color: 'var(--text-2)' }}>Activez l'application pour accéder au paramétrage (inscriptions sourceurs, durée de propriété des CV).</p>
          </div>
        ))}
        {app.key === 'nda' && (active ? (
          <NdaConfig showNotification={showNotification} docsignActive={isActive('docsign')} />
        ) : (
          <div style={{ ...card, padding: '28px 24px', textAlign: 'center' }}>
            <BI icon="lock" style={{ fontSize: '20px', color: 'var(--text-faint)' }} />
            <p style={{ margin: '8px 0 0', fontSize: '13px', color: 'var(--text-2)' }}>Activez l'application (signd requis) pour définir le template DOCX de votre NDA.</p>
          </div>
        ))}
        {app.key === 'linkedin_publication' && (active ? (
          <LinkedInModule showNotification={showNotification} />
        ) : (
          <div style={{ ...card, padding: '28px 24px', textAlign: 'center' }}>
            <BI icon="lock" style={{ fontSize: '20px', color: 'var(--text-faint)' }} />
            <p style={{ margin: '8px 0 0', fontSize: '13px', color: 'var(--text-2)' }}>Activez l'application pour relier les comptes LinkedIn et publier vos annonces.</p>
          </div>
        ))}
        {app.key === 'assistant_site' && (active ? (
          <AssistantSiteModule showNotification={showNotification} />
        ) : (
          <div style={{ ...card, padding: '28px 24px', textAlign: 'center' }}>
            <BI icon="lock" style={{ fontSize: '20px', color: 'var(--text-faint)' }} />
            <p style={{ margin: '8px 0 0', fontSize: '13px', color: 'var(--text-2)' }}>Activez l'application pour créer l'assistant, écrire ce qu'il sait et obtenir la ligne à coller dans votre site.</p>
          </div>
        ))}
        {app.key === 'docsign' && (active ? (
          <DocsignConfig showNotification={showNotification} />
        ) : (
          <div style={{ ...card, padding: '28px 24px', textAlign: 'center' }}>
            <BI icon="lock" style={{ fontSize: '20px', color: 'var(--text-faint)' }} />
            <p style={{ margin: '8px 0 0', fontSize: '13px', color: 'var(--text-2)' }}>Activez l'application pour raccorder votre instance signd (URL, clés, test de connexion).</p>
          </div>
        ))}
      </div>
    );
  }

  // ── Liste des apps ──
  return (
    <div style={{ fontFamily: "'IBM Plex Sans', system-ui, sans-serif" }}>
      <div style={{ marginBottom: '20px' }}>
        <h1 style={{ margin: 0, fontFamily: "'Archivo',sans-serif", fontSize: '26px', fontWeight: 800, letterSpacing: '-0.025em', color: 'var(--text-strong)' }}>Marketplace<span style={{ color: 'var(--brand)' }}>.</span></h1>
        <p style={{ margin: '5px 0 0', fontSize: '13px', color: 'var(--text-2)' }}>Activez des applications pour votre organisation — leurs fonctionnalités et leur paramétrage s'intègrent directement dans l'ERP.</p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '14px' }}>
        {APPS.map(a => {
          const active = isActive(a.key);
          return (
            <div key={a.key} onClick={() => setOpenApp(a.key)} {...(a.key === 'assistant_site' ? ancre('assistant-site') : {})}
              style={{ ...card, padding: '18px 20px', cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <Monogram text={a.monogram} size={44} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '15.5px', fontWeight: 700, color: 'var(--text-strong)', letterSpacing: '-0.01em' }}>{a.name}<span style={{ color: 'var(--brand)' }}>.</span></div>
                  <div style={{ fontSize: '12px', color: 'var(--text-2)', marginTop: '1px' }}>{a.tagline}</div>
                </div>
                <span style={{ flex: 'none', fontSize: '11px', fontWeight: 600, padding: '3px 9px', borderRadius: '6px', background: active ? 'rgba(45,200,110,0.15)' : 'var(--chip-bg)', color: active ? '#16a34a' : 'var(--text-2)' }}>{active ? 'Activée' : 'Désactivée'}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '11px', fontWeight: 600, padding: '3px 9px', borderRadius: '6px', background: 'var(--chip-bg)', color: 'var(--text-2)' }}>{a.category}</span>
                <span style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: '11.5px', color: 'var(--text-3)' }}>{a.price}</span>
                <span style={{ flex: 1 }} />
                <span style={{ fontSize: '12.5px', fontWeight: 600, color: 'var(--text-2)', display: 'inline-flex', alignItems: 'center', gap: '5px' }}>Ouvrir <BI icon="chevron-right" style={{ fontSize: '11px' }} /></span>
              </div>
            </div>
          );
        })}

        {/* Emplacement des prochaines applications */}
        <div style={{ border: '1px dashed var(--input-border)', borderRadius: '14px', padding: '18px 20px', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '120px', color: 'var(--text-faint)', fontSize: '12.5px' }}>
          D'autres applications arrivent bientôt.
        </div>
      </div>
    </div>
  );
}

// ── Paramétrage du module NDA : template DOCX de l'organisation ──────────────
// GET /config/nda/template → { name, updated_at, is_default } ; remplacement
// POST multipart (champ `template`, .docx) ; DELETE → retour au template par
// défaut erpia. Variables injectées à l'envoi (depuis le besoin/candidat).
// ── Paramétrage du module docsign (marque : signd) : service en ligne ─────────────
// Rien à installer : la signature tourne sur signd (service mutualisé,
// adresse fixe). Une clé UNIQUE par organisation, générée UNE seule fois et
// non modifiable — le backend erpia la synchronise lui-même avec signd
// (clés stockées par org dans la BDD signd). Contrat : GET /config/docsign
// → { api_key_masked, keys_generated_at, last_webhook_at } ; POST
// /config/docsign/keys (une fois, 409 si déjà générée) → { api_key,
// generated_at } ; POST /config/docsign/test → { connected, emails_enabled,
// reason?, last_webhook_at? }.
const dsMask = (k) => {
  const s = String(k || '');
  if (!s) return '';
  if (s.includes('…')) return s; // déjà masquée côté serveur
  return s.length <= 12 ? s : `${s.slice(0, 8)}…${s.slice(-4)}`;
};
const dsDate = (d) => { try { return new Date(d).toLocaleString('fr-CH', { dateStyle: 'medium', timeStyle: 'short' }); } catch { return String(d); } };

function DocsignConfig({ showNotification }) {
  const [cfg, setCfg] = useState(undefined);   // undefined = chargement, null = routes absentes
  const [genBusy, setGenBusy] = useState(false);
  const [reveal, setReveal] = useState(null);  // { api_key, generated_at } — affichée UNE fois
  const [testBusy, setTestBusy] = useState(false);
  const [test, setTest] = useState(null);      // { ok, emails, reason, at }

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${BASE}/config/docsign`, { headers: authH() });
      if (r.status === 404) { setCfg(null); return; }
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const j = await r.json().catch(() => ({}));
      setCfg(j.data || j);
    } catch { setCfg(null); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const copy = async (label, value) => {
    try { await navigator.clipboard.writeText(value); showNotification?.(`${label} copiée.`, 'success'); }
    catch { await alertErpia({ title: `Copier ${label.toLowerCase()}`, message: 'Le presse-papiers est indisponible. Sélectionnez puis copiez la valeur ci-dessous.', detail: value }); }
  };

  // Génération UNIQUE : la clé est créée une fois pour l'organisation puis
  // synchronisée automatiquement avec signd par le backend. Pas de
  // régénération côté interface (clé non modifiable).
  const generateKey = async () => {
    setGenBusy(true);
    try {
      const r = await fetch(`${BASE}/config/docsign/keys`, { method: 'POST', headers: authH() });
      const j = await r.json().catch(() => ({}));
      if (r.status === 409 || j.already) { showNotification?.(j.message || 'Votre clé existe déjà — elle est unique et non modifiable.', 'error'); load(); return; }
      if (!r.ok || j.success === false) throw new Error(j.error || `HTTP ${r.status}`);
      const d = j.data || j;
      setReveal({ api_key: d.api_key, generated_at: d.generated_at });
      showNotification?.('Clé générée — votre organisation est synchronisée avec signd.', 'success');
      load();
    } catch (e) { showNotification?.('Erreur génération de la clé : ' + e.message, 'error'); }
    finally { setGenBusy(false); }
  };

  // Ouverture SSO du tableau de bord signd : JAMAIS de lien direct — le
  // backend génère à CHAQUE clic un jeton 2 min à usage unique auprès de
  // signd et renvoie l'url de connexion (rien n'est stocké ni mis en
  // cache côté front). Onglet ouvert AVANT le fetch (anti popup-blocker).
  const [ssoBusy, setSsoBusy] = useState(false);
  const openDocsign = async () => {
    if (ssoBusy) return;
    setSsoBusy(true);
    const w = window.open('about:blank', '_blank');
    try {
      const r = await fetch(`${BASE}/admin/integrations/docsign/open`, {
        headers: { ...authH(), Accept: 'application/json' },
        redirect: 'manual',
      });
      // Le serveur répond en JSON quand on le lui demande (Accept) : l'ancien
      // repli qui rejouait l'adresse AVEC le jeton de session dans l'onglet
      // est retiré (28.09) — la route n'acceptait d'ailleurs pas le jeton en
      // adresse, et une session ne voyage plus dans une URL.
      if (r.type === 'opaqueredirect') throw new Error('la connexion n\'a pas pu être préparée — réessayez.');
      if (r.status === 404) throw new Error('l\'ouverture automatique n\'est pas encore en ligne côté serveur (route /admin/integrations/docsign/open à déployer) — le bouton fonctionnera dès son déploiement, sans mise à jour de l\'interface.');
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j.success === false) throw new Error(j.error || `HTTP ${r.status}`);
      const url = j.url || j.data?.url;
      if (!url) throw new Error('réponse sans url de connexion');
      if (w) w.location.replace(url); else window.open(url, '_blank', 'noopener');
    } catch (e) {
      try { w?.close(); } catch { /* onglet déjà parti */ }
      showNotification?.('Impossible d\'ouvrir signd : ' + e.message, 'error');
    } finally { setSsoBusy(false); }
  };

  const runTest = async () => {
    setTestBusy(true);
    try {
      const r = await fetch(`${BASE}/config/docsign/test`, { method: 'POST', headers: authH() });
      const j = await r.json().catch(() => ({}));
      const d = j.data || j;
      const ok = d.connected === true || (r.ok && d.connected !== false && j.success !== false);
      setTest({
        ok,
        emails: d.emails_enabled,
        reason: ok ? null : (d.reason || d.error || j.error || `HTTP ${r.status}`),
        hint: ok ? null : (d.hint || null),          // guide de résolution (ex. → réinitialisation)
        keySource: d.key_source ?? null,
        at: d.last_webhook_at ?? null,
      });
      if (d.last_webhook_at) setCfg(c => (c ? { ...c, last_webhook_at: d.last_webhook_at } : c));
    } catch (e) { setTest({ ok: false, reason: e.message, emails: undefined, at: null }); }
    finally { setTestBusy(false); }
  };

  const card = { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '14px' };
  const secLbl = { fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-3)', marginBottom: '10px' };
  const mono = { fontFamily: "'IBM Plex Mono',monospace" };
  const primBtn = (busy) => ({ display: 'inline-flex', alignItems: 'center', gap: '7px', padding: '9px 15px', borderRadius: '8px', border: 'none', background: 'var(--accent)', color: 'var(--accent-fg)', fontFamily: 'inherit', fontSize: '13px', fontWeight: 600, cursor: busy ? 'wait' : 'pointer', opacity: busy ? 0.6 : 1, flex: 'none' });

  // Réinitialisation (zone danger) : efface les clés côté erpia — l'activation
  // repart de zéro avec « Générer ».
  const [confirmReset, setConfirmReset] = useState(false);
  const [resetBusy, setResetBusy] = useState(false);
  const resetKeys = async () => {
    setConfirmReset(false);
    setResetBusy(true);
    try {
      const r = await fetch(`${BASE}/config/docsign/keys`, { method: 'DELETE', headers: authH() });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j.success === false) throw new Error(j.error || `HTTP ${r.status}`);
      setReveal(null); setTest(null);
      showNotification?.(j.message || (j.had_key ? 'Clés effacées — relancez l\'activation avec « Générer ma clé ».' : 'Aucune clé à effacer.'), 'success');
      load();
    } catch (e) { showNotification?.('Erreur réinitialisation : ' + e.message, 'error'); }
    finally { setResetBusy(false); }
  };

  const maskedKey = reveal ? dsMask(reveal.api_key) : dsMask(cfg?.api_key_masked || cfg?.api_key);
  const hasKey = !!(reveal || maskedKey);
  const generatedAt = reveal?.generated_at || cfg?.keys_generated_at;
  const lastWebhookAt = test?.at || cfg?.last_webhook_at;
  const keySource = test?.keySource ?? cfg?.key_source ?? null; // 'org' | 'env' | 'global' | null

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      {cfg === null && (
        <div style={{ padding: '10px 14px', borderRadius: '9px', background: 'var(--code-bg)', border: '1px solid var(--hair)', color: 'var(--text-2)', fontSize: '12.5px' }}>
          <BI icon="info-circle" style={{ marginRight: '7px' }} />La connexion s'active bientôt — le panneau est prêt, tout se fera automatiquement.
        </div>
      )}

      {/* 1 ─ Le service : adresse fixe, rien à installer */}
      <div style={{ ...card, padding: '20px 24px', display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap' }}>
        <div style={{ width: '42px', height: '42px', borderRadius: '11px', background: 'var(--text-strong)', color: 'var(--bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 'none' }}><BI icon="pen" /></div>
        <div style={{ flex: 1, minWidth: '220px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '9px', flexWrap: 'wrap' }}>
            <span className="ats-mono" style={{ fontSize: '15px', fontWeight: 600, color: 'var(--text-strong)' }}>signd</span>
            <span style={{ fontSize: '11px', fontWeight: 600, padding: '3px 9px', borderRadius: '6px', background: 'rgba(45,200,110,0.15)', color: '#16a34a' }}>Service en ligne — rien à installer</span>
          </div>
          <div style={{ fontSize: '12.5px', color: 'var(--text-2)', marginTop: '4px' }}>Votre signature électronique est fournie par signd. Il vous suffit d'une clé — tout le reste est automatique.</div>
        </div>
        <button onClick={openDocsign} disabled={ssoBusy || !hasKey} style={{ ...primBtn(ssoBusy), opacity: ssoBusy ? 0.6 : hasKey ? 1 : 0.5, cursor: ssoBusy ? 'wait' : hasKey ? 'pointer' : 'not-allowed' }}
          title={hasKey ? 'Ouvrir votre tableau de bord signd — connexion automatique' : 'Générez d\'abord votre clé ci-dessous'}>
          <BI icon={ssoBusy ? 'arrow-repeat' : 'box-arrow-up-right'} /> {ssoBusy ? 'Connexion…' : 'Ouvrir signd'}
        </button>
      </div>

      {/* 2 ─ La clé : unique, générée une fois, synchronisée automatiquement */}
      <div style={{ ...card, padding: '20px 24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap' }}>
          <div style={{ ...secLbl, marginBottom: 0 }}>Votre clé signd</div>
          {!hasKey && (
            <button onClick={generateKey} disabled={genBusy || cfg === undefined} style={primBtn(genBusy)}>
              <BI icon={genBusy ? 'arrow-repeat' : 'key'} /> {genBusy ? 'Génération…' : 'Générer ma clé'}
            </button>
          )}
        </div>

        {reveal ? (
          <div style={{ marginTop: '14px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
              <code style={{ ...mono, fontSize: '12.5px', fontWeight: 600, color: 'var(--text-strong)', background: 'var(--code-bg)', border: '1px solid var(--hair)', borderRadius: '7px', padding: '7px 11px', flex: 1, minWidth: '220px', overflowWrap: 'anywhere' }}>{reveal.api_key}</code>
              <button onClick={() => copy('Clé', reveal.api_key)} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '7px 12px', borderRadius: '7px', border: '1px solid var(--input-border)', background: 'var(--ghost-bg)', color: 'var(--text)', fontFamily: 'inherit', fontSize: '12px', fontWeight: 600, cursor: 'pointer', flex: 'none' }}><BI icon="clipboard" /> Copier</button>
            </div>
            <div style={{ fontSize: '12.5px', color: '#16a34a', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '7px' }}>
              <BI icon="check-circle-fill" /> Clé synchronisée avec signd — vous n'avez rien d'autre à faire.
            </div>
          </div>
        ) : hasKey ? (
          <div style={{ marginTop: '14px', display: 'flex', alignItems: 'center', gap: '11px', padding: '11px 14px', borderRadius: '10px', background: 'var(--code-bg)', flexWrap: 'wrap' }}>
            <BI icon="key-fill" style={{ fontSize: '15px', color: 'var(--text-2)', flex: 'none' }} />
            <code style={{ ...mono, fontSize: '13px', fontWeight: 600, color: 'var(--text-strong)' }}>{maskedKey}</code>
            <span style={{ fontSize: '12px', color: 'var(--text-3)' }}>{generatedAt ? `générée le ${dsDate(generatedAt)} · ` : ''}unique et non modifiable</span>
          </div>
        ) : (
          <div style={{ marginTop: '10px', fontSize: '12.5px', color: 'var(--text-2)', lineHeight: 1.6 }}>
            Générez la clé de votre organisation — une seule fois. Elle connecte erpia à signd automatiquement : aucune manipulation de votre côté.
          </div>
        )}

        {/* Provenance de la clé : env = variable serveur (Railway), pas une clé d'org */}
        {keySource === 'env' && (
          <div style={{ marginTop: '12px', padding: '10px 13px', borderRadius: '9px', border: '1px solid rgba(217,119,6,0.45)', background: 'rgba(217,119,6,0.07)', color: '#d97706', fontSize: '12.5px', fontWeight: 600 }}>
            <BI icon="exclamation-triangle-fill" style={{ marginRight: '7px' }} />Clé issue de l'environnement serveur — ce n'est pas une clé propre à votre organisation. Réinitialisez puis générez votre clé.
          </div>
        )}
        {keySource === 'global' && (
          <div style={{ marginTop: '12px', fontSize: '12px', color: 'var(--text-3)' }}>
            <BI icon="globe2" style={{ marginRight: '6px' }} />Clé globale partagée — générez une clé propre à votre organisation pour un fonctionnement isolé.
          </div>
        )}

        {/* Zone danger : réinitialisation (efface les clés → activation à relancer) */}
        {(hasKey || keySource) && (
          <div style={{ marginTop: '14px', paddingTop: '12px', borderTop: '1px solid var(--hair)', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '11.5px', color: 'var(--text-3)', flex: 1, minWidth: '220px' }}>Zone danger — efface les clés côté erpia ; il faudra relancer l'activation.</span>
            <button onClick={() => setConfirmReset(true)} disabled={resetBusy} className="src-row-hover"
              style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '7px 12px', borderRadius: '8px', border: '1px solid var(--danger-bd)', background: 'transparent', color: 'var(--danger-fg)', fontFamily: 'inherit', fontSize: '12px', fontWeight: 600, cursor: resetBusy ? 'wait' : 'pointer', opacity: resetBusy ? 0.6 : 1, flex: 'none' }}>
              <BI icon={resetBusy ? 'arrow-repeat' : 'arrow-counterclockwise'} /> Réinitialiser les clés
            </button>
          </div>
        )}
      </div>

      {/* 3 ─ Vérifier la connexion */}
      <div style={{ ...card, padding: '20px 24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap' }}>
          <div style={{ ...secLbl, marginBottom: 0 }}>Vérifier la connexion</div>
          <button onClick={runTest} disabled={testBusy} style={primBtn(testBusy)}>
            <BI icon={testBusy ? 'arrow-repeat' : 'activity'} /> {testBusy ? 'Vérification…' : 'Vérifier'}
          </button>
        </div>
        {test && (
          <div style={{ marginTop: '12px', display: 'inline-flex', alignItems: 'center', gap: '9px', padding: '9px 13px', borderRadius: '9px', fontSize: '12.5px', fontWeight: 600, background: test.ok ? 'rgba(22,163,74,0.12)' : 'var(--danger-bg)', color: test.ok ? '#16a34a' : 'var(--danger-fg)' }}>
            <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: 'currentColor', flex: 'none' }} />
            {test.ok
              ? `Connecté${test.emails !== undefined ? ` (envoi d'emails : ${test.emails ? 'activé' : 'désactivé'})` : ''}`
              : `Pas encore connecté — ${test.reason}`}
          </div>
        )}
        {test && !test.ok && test.hint && (
          <div style={{ marginTop: '8px', fontSize: '12px', color: 'var(--text-2)', display: 'flex', alignItems: 'flex-start', gap: '7px' }}>
            <BI icon="info-circle" style={{ color: '#d97706', marginTop: '1px', flex: 'none' }} />
            <span>{test.hint}</span>
          </div>
        )}
        <div style={{ marginTop: '10px', fontSize: '12px', color: 'var(--text-3)', display: 'flex', alignItems: 'center', gap: '7px' }}>
          <BI icon="broadcast-pin" />
          {lastWebhookAt
            ? <>Dernier signe de vie de signd : <b style={{ color: 'var(--text-2)' }}>{dsDate(lastWebhookAt)}</b> — la liaison fonctionne dans les deux sens.</>
            : 'Aucun signe de vie de signd pour l\'instant — il apparaîtra dès le premier document signé.'}
        </div>
      </div>

      {/* Modale de confirmation — réinitialisation des clés */}
      {confirmReset && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 90, padding: '16px' }}
          onClick={() => setConfirmReset(false)}>
          <div style={{ ...card, width: '100%', maxWidth: '440px', padding: '22px 24px' }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
              <BI icon="exclamation-triangle-fill" style={{ fontSize: '17px', color: '#d97706' }} />
              <span style={{ fontSize: '15px', fontWeight: 700, color: 'var(--text-strong)' }}>Réinitialiser les clés ?</span>
            </div>
            <p style={{ margin: 0, fontSize: '13px', lineHeight: 1.6, color: 'var(--text-2)' }}>
              Les clés locales seront <b style={{ color: 'var(--text)' }}>effacées</b> ; il faudra relancer l'activation (« Générer ma clé ») pour que la signature refonctionne.
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '18px' }}>
              <button onClick={() => setConfirmReset(false)}
                style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '9px 14px', borderRadius: '8px', border: '1px solid var(--input-border)', background: 'var(--ghost-bg)', color: 'var(--text)', fontFamily: 'inherit', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}>
                Annuler
              </button>
              <button onClick={resetKeys}
                style={{ display: 'inline-flex', alignItems: 'center', gap: '7px', padding: '9px 15px', borderRadius: '8px', border: 'none', background: 'var(--brand)', color: '#fff', fontFamily: 'inherit', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}>
                <BI icon="arrow-counterclockwise" /> Réinitialiser
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const NDA_VARS = [
  ['{{client}}', 'Client du besoin (l\'opportunité)'],
  ['{{candidat_prenom}}', 'Prénom du candidat'],
  ['{{candidat_nom}}', 'Nom du candidat'],
  ['{{societe}}', 'Votre organisation'],
  ['{{date}}', 'Date d\'envoi'],
];
function NdaConfig({ showNotification, docsignActive }) {
  const [tpl, setTpl] = useState(undefined); // undefined = chargement, null = routes absentes
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${BASE}/config/nda/template`, { headers: authH() });
      if (r.status === 404) { setTpl(null); return; }
      if (r.ok) { const j = await r.json().catch(() => ({})); setTpl(j.data || j); }
    } catch { setTpl(null); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const upload = async (file) => {
    if (!file) return;
    if (!/\.docx$/i.test(file.name)) { showNotification?.('Le template doit être un fichier .docx.', 'error'); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('template', file);
      const r = await fetch(`${BASE}/config/nda/template`, { method: 'POST', headers: authH(), body: fd });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j.success === false) throw new Error(j.error || `HTTP ${r.status}`);
      showNotification?.(j.message || 'Template NDA remplacé.', 'success');
      load();
    } catch (e) { showNotification?.('Erreur template : ' + e.message, 'error'); }
    finally { setBusy(false); }
  };
  const resetDefault = async () => {
    setBusy(true);
    try {
      const r = await fetch(`${BASE}/config/nda/template`, { method: 'DELETE', headers: authH() });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j.success === false) throw new Error(j.error || `HTTP ${r.status}`);
      showNotification?.(j.message || 'Retour au template par défaut erpia.', 'success');
      load();
    } catch (e) { showNotification?.('Erreur : ' + e.message, 'error'); }
    finally { setBusy(false); }
  };

  const card = { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '14px' };
  return (
    <div style={{ ...card, padding: '20px 24px' }}>
      {!docsignActive && (
        <div style={{ marginBottom: '14px', padding: '10px 14px', borderRadius: '9px', background: 'var(--danger-bg)', border: '1px solid var(--danger-bd)', color: 'var(--danger-fg)', fontSize: '12.5px' }}>
          <BI icon="exclamation-triangle" style={{ marginRight: '7px' }} />signd est désactivé — le NDA ne peut pas partir en signature tant qu'il n'est pas réactivé.
        </div>
      )}
      <div style={{ fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-3)', marginBottom: '10px' }}>Template DOCX</div>
      <input ref={fileRef} type="file" accept=".docx" style={{ display: 'none' }} onChange={e => { upload(e.target.files?.[0]); e.target.value = ''; }} />
      <div style={{ display: 'flex', alignItems: 'center', gap: '11px', padding: '12px 14px', borderRadius: '10px', background: 'var(--code-bg)', flexWrap: 'wrap' }}>
        <BI icon="file-earmark-word" style={{ fontSize: '18px', color: 'var(--text-2)', flex: 'none' }} />
        <div style={{ flex: 1, minWidth: '200px' }}>
          <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-strong)' }}>
            {tpl === undefined ? 'Chargement…' : tpl === null || tpl?.is_default !== false ? 'Template par défaut erpia' : (tpl.name || 'Template personnalisé')}
          </div>
          <div style={{ fontSize: '11.5px', color: 'var(--text-3)', marginTop: '1px' }}>
            {tpl === null ? 'Le stockage du template arrive côté serveur — le défaut erpia est utilisé en attendant.'
              : tpl?.is_default === false ? `Template de votre organisation${tpl.updated_at ? ` · mis à jour le ${new Date(tpl.updated_at).toLocaleDateString('fr-CH')}` : ''}`
                : 'NDA générique bilingue fourni par erpia — remplacez-le par le vôtre.'}
          </div>
        </div>
        <button onClick={() => fileRef.current?.click()} disabled={busy}
          style={{ display: 'inline-flex', alignItems: 'center', gap: '7px', padding: '8px 13px', borderRadius: '8px', border: 'none', background: 'var(--accent)', color: 'var(--accent-fg)', fontFamily: 'inherit', fontSize: '12.5px', fontWeight: 600, cursor: busy ? 'wait' : 'pointer', opacity: busy ? 0.6 : 1 }}>
          <BI icon={busy ? 'arrow-repeat' : 'upload'} /> Remplacer (.docx)
        </button>
        {tpl?.is_default === false && (
          <button onClick={resetDefault} disabled={busy}
            style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '8px 12px', borderRadius: '8px', border: '1px solid var(--input-border)', background: 'var(--ghost-bg)', color: 'var(--text)', fontFamily: 'inherit', fontSize: '12.5px', fontWeight: 600, cursor: 'pointer' }}>
            <BI icon="arrow-counterclockwise" /> Défaut erpia
          </button>
        )}
      </div>
      <div style={{ fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-3)', margin: '16px 0 8px' }}>Variables injectées à l'envoi</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
        {NDA_VARS.map(([v, d]) => (
          <div key={v} style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '12.5px' }}>
            <code style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: '12px', fontWeight: 600, color: 'var(--text-strong)', background: 'var(--code-bg)', padding: '2px 8px', borderRadius: '6px', flex: 'none' }}>{v}</code>
            <span style={{ color: 'var(--text-2)' }}>{d}</span>
          </div>
        ))}
      </div>
      <div style={{ marginTop: '12px', fontSize: '11.5px', color: 'var(--text-3)', lineHeight: 1.55 }}>
        Le NDA s'envoie depuis le <b style={{ color: 'var(--text)' }}>candidat positionné sur un besoin</b> (bouton « Envoyer le NDA ») — c'est le client de CE besoin qui est injecté dans le template, puis le document part en signature via signd.
      </div>
      {/* Rendu PDF simplifié tant que la conversion complète (LibreOffice)
          n'est pas sur le serveur — à retirer quand elle y sera. */}
      <div style={{ marginTop: '8px', fontSize: '11.5px', color: 'var(--text-3)', lineHeight: 1.55, display: 'flex', alignItems: 'flex-start', gap: '7px' }}>
        <BI icon="info-circle" style={{ marginTop: '1px', flex: 'none' }} />
        <span>Le PDF envoyé en signature reprend le texte du template (titre + sections) dans une mise en page épurée — sans logo ni graphismes pour l'instant. La mise en forme complète du Word arrivera automatiquement, rien à refaire de votre côté.</span>
      </div>
    </div>
  );
}
